import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFocusMode } from './FocusModeContext';
import DisclosureCaret from './components/DisclosureCaret';
import './App.css';

export default function Navbar({ userName, handleSignOut }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const { focusMode, toggleFocusMode } = useFocusMode();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');

  const hideContinueOnThisPage = location.pathname.startsWith('/subscriber-dashboard');
  const isHomePage = location.pathname === '/';
  const isRequestDemoLanding = location.pathname === '/request-demo-class';
  const accountRole = String(localStorage.getItem('role') || '').trim().toLowerCase();
  const canShareParentFeedback = accountRole === 'parent' || accountRole === 'subscriber';
  const mobileUserName = getMobileUserName(userName);

  function normalizeGrade(g) {
    if (!g) return '';
    const s = String(g).trim();
    if (s.toUpperCase() === 'KG' || s.toLowerCase() === 'kindergarten') return 'KG';
    const match = s.match(/^(\d+)/);
    return match ? match[1] : '';
  }

  useEffect(() => {
    const sub = localStorage.getItem('subscribedSubject');
    const gr = localStorage.getItem('subscribedGrade');
    if (sub) setSelectedSubject(sub);
    if (gr) setSelectedGrade(normalizeGrade(gr));
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowAccountMenu(false);
        setShowMoreMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const getSubjectSlug = (subj) => {
    if (!subj) return '';
    const s = subj.trim().toLowerCase();
    if (['math', 'maths', 'mathematics'].includes(s)) return 'Math';
    if (['science', 'sciences'].includes(s)) return 'Science';
    return subj.replace(/\s+/g, '');
  };

  const handleContinueQuiz = () => {
    const subject = selectedSubject || localStorage.getItem('subscribedSubject');
    const rawGrade = selectedGrade || localStorage.getItem('subscribedGrade');
    const grade = normalizeGrade(rawGrade);

    if (!subject || !grade) {
      alert('Please set a valid subject and grade before continuing.');
      setShowSelect(true);
      setShowMoreMenu(true);
      return;
    }

    const slug = getSubjectSlug(subject);
    if (grade === 'KG') {
      navigate(`/quiz/${slug}/KG`);
      return;
    }

    if (isNaN(Number(grade))) {
      alert('Please set a valid subject and grade before continuing.');
      setShowSelect(true);
      setShowMoreMenu(true);
      return;
    }

    navigate(`/quiz/${slug}/${ordinal(Number(grade))}Grade`);
  };

  const handleResetGrade = () => {
    localStorage.removeItem('subscribedGrade');
    const previousSubject = localStorage.getItem('subscribedSubject');

    if (previousSubject === 'Maths') {
      localStorage.setItem('subscribedSubject', 'Math');
      navigate('/subject/Math');
      return;
    }

    localStorage.removeItem('subscribedSubject');
    navigate('/subject');
  };

  const handleManageAccount = () => {
    const plan = (localStorage.getItem('subscriptionStatus') || '').trim().toLowerCase();
    const subscriberPlans = ['monthly', 'yearly', 'freetrial'];
    setShowAccountMenu(false);
    navigate(subscriberPlans.includes(plan) ? '/subscriber-dashboard' : '/signup-home');
  };

  const handleGoDashboard = () => {
    const role = String(localStorage.getItem('userType') || localStorage.getItem('role') || '').trim().toLowerCase();
    navigate(role === 'tutor' ? '/tutor-dashboard' : '/subscriber-dashboard');
  };

  const handleSaveAndStart = () => {
    if (!selectedSubject || !selectedGrade) {
      alert('Please select both subject and grade.');
      return;
    }

    localStorage.setItem('subscribedSubject', selectedSubject);
    localStorage.setItem('subscribedGrade', selectedGrade);
    setShowSelect(false);
    setShowMoreMenu(false);
    handleContinueQuiz();
  };

  const openAccountMenu = () => {
    setShowMoreMenu(false);
    setShowAccountMenu((value) => !value);
  };

  const openMoreMenu = () => {
    setShowAccountMenu(false);
    setShowMoreMenu((value) => !value);
  };

  if (isRequestDemoLanding) {
    return (
      <header className="site-nav site-nav--demoLanding" aria-label="TutorOrca">
        <div className="site-nav__inner site-nav__inner--demoLanding">
          <button className="site-nav__brand" type="button" onClick={() => navigate('/')} aria-label="TutorOrca home">
            <img src="/images/misc/tutororca-logo-clean.jpg" alt="TutorOrca" width="156" height="64" decoding="async" fetchpriority="high" />
          </button>

          <nav className="site-nav__links site-nav__links--demoLanding" aria-label="Helpful pages">
            <Link to="/subject">Subjects</Link>
            <Link to="/features">Features</Link>
            <Link to="/about">About</Link>
          </nav>

          <div className={`site-nav__right site-nav__right--demoLanding ${userName ? 'site-nav__right--signedIn' : 'site-nav__right--guest'}`} ref={dropdownRef}>
            <a
              className="site-nav__whatsapp"
              href="https://wa.me/14256864893"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with TutorOrca on WhatsApp"
            >
              <img src="/images/misc/whatsapp-svgrepo-com.svg" alt="" width="16" height="16" />
              <span className="site-nav__whatsappNumber">+1 425 686 4893</span>
            </a>

            {userName ? (
              <>
                <button className="site-nav__dashboard" type="button" onClick={handleGoDashboard}>
                  Go to my dashboard
                </button>

                <div className="site-nav__menuAnchor site-nav__menuAnchor--account">
                  <button
                    className="site-nav__hello"
                    type="button"
                    onClick={openAccountMenu}
                    title={`Hello, ${userName}`}
                    aria-label={`Account menu for ${userName}`}
                  >
                    <span className="site-nav__helloLabelDesktop">Hello, {userName}</span>
                    <span className="site-nav__helloLabelMobile">Hi, {mobileUserName}</span>
                    <DisclosureCaret open={showAccountMenu} />
                  </button>

                  {showAccountMenu && (
                    <AccountMenu
                      handleManageAccount={handleManageAccount}
                      canShareParentFeedback={canShareParentFeedback}
                      closeMenu={() => setShowAccountMenu(false)}
                      handleSignOut={handleSignOut}
                      navigate={navigate}
                    />
                  )}
                </div>
              </>
            ) : (
              <Link className="site-nav__login" to="/login">Log In</Link>
            )}

            <div className="site-nav__menuAnchor site-nav__menuAnchor--more">
              <button className="site-nav__more" type="button" onClick={openMoreMenu}>
                More <DisclosureCaret open={showMoreMenu} />
              </button>

              {showMoreMenu && (
                <MoreMenu
                  showSelect={showSelect}
                  selectedSubject={selectedSubject}
                  selectedGrade={selectedGrade}
                  setSelectedSubject={setSelectedSubject}
                  setSelectedGrade={setSelectedGrade}
                  handleSaveAndStart={handleSaveAndStart}
                  userName={userName}
                  toggleFocusMode={toggleFocusMode}
                  focusMode={focusMode}
                  handleResetGrade={handleResetGrade}
                  closeMenu={() => setShowMoreMenu(false)}
                />
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`site-nav ${isHomePage ? 'site-nav--home' : ''}`} aria-label="TutorOrca">
      <div className="site-nav__inner">
        <div className="site-nav__left">
          <button className="site-nav__brand" type="button" onClick={() => navigate('/')} aria-label="TutorOrca home">
            <img src="/images/misc/tutororca-logo-clean.jpg" alt="TutorOrca" width="156" height="64" decoding="async" fetchpriority="high" />
          </button>

          <nav className="site-nav__links" aria-label="Primary navigation">
            <Link to="/subject">Subjects</Link>
            <Link to="/features">Features</Link>
            <Link to="/about">About</Link>
          </nav>
        </div>

        <div className={`site-nav__right ${userName ? 'site-nav__right--signedIn' : 'site-nav__right--guest'}`} ref={dropdownRef}>
          <button className="site-nav__demo" type="button" onClick={() => navigate('/request-demo-class')}>
            <span className="site-nav__demoLabelDesktop">Reserve a Free Demo Class</span>
            <span className="site-nav__demoLabelMobile">Reserve a Free Demo</span>
          </button>

          <a
            className="site-nav__whatsapp"
            href="https://wa.me/14256864893"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat with TutorOrca on WhatsApp"
          >
            <img src="/images/misc/whatsapp-svgrepo-com.svg" alt="" width="16" height="16" />
            <span className="site-nav__whatsappNumber">+1 425 686 4893</span>
          </a>

          {userName ? (
            <>
              <button className="site-nav__dashboard" type="button" onClick={handleGoDashboard}>
                Go to my dashboard
              </button>

              <div className="site-nav__menuAnchor site-nav__menuAnchor--account">
                <button
                  className="site-nav__hello"
                  type="button"
                  onClick={openAccountMenu}
                  title={`Hello, ${userName}`}
                  aria-label={`Account menu for ${userName}`}
                >
                  <span className="site-nav__helloLabelDesktop">Hello, {userName}</span>
                  <span className="site-nav__helloLabelMobile">Hi, {mobileUserName}</span>
                  <DisclosureCaret open={showAccountMenu} />
                </button>

                {showAccountMenu && (
                  <AccountMenu
                    handleManageAccount={handleManageAccount}
                    canShareParentFeedback={canShareParentFeedback}
                    closeMenu={() => setShowAccountMenu(false)}
                    handleSignOut={handleSignOut}
                    navigate={navigate}
                  />
                )}
              </div>

              {!hideContinueOnThisPage && (
                <button className="site-nav__quiz" type="button" onClick={handleContinueQuiz}>
                  Continue Quiz
                </button>
              )}

              <div className="site-nav__menuAnchor site-nav__menuAnchor--more">
                <button className="site-nav__more" type="button" onClick={openMoreMenu}>
                  More <DisclosureCaret open={showMoreMenu} />
                </button>

                {showMoreMenu && (
                  <MoreMenu
                    showSelect={showSelect}
                    selectedSubject={selectedSubject}
                    selectedGrade={selectedGrade}
                    setSelectedSubject={setSelectedSubject}
                    setSelectedGrade={setSelectedGrade}
                    handleSaveAndStart={handleSaveAndStart}
                    userName={userName}
                    toggleFocusMode={toggleFocusMode}
                    focusMode={focusMode}
                    handleResetGrade={handleResetGrade}
                    closeMenu={() => setShowMoreMenu(false)}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <Link className="site-nav__login" to="/login">Log In</Link>
              <Link className="site-nav__login" to="/tutor-apply">Join as a Tutor</Link>
              <div className="site-nav__menuAnchor site-nav__menuAnchor--more">
                <button className="site-nav__more" type="button" onClick={openMoreMenu}>
                  More <DisclosureCaret open={showMoreMenu} />
                </button>

                {showMoreMenu && (
                  <MoreMenu
                    showSelect={showSelect}
                    selectedSubject={selectedSubject}
                    selectedGrade={selectedGrade}
                    setSelectedSubject={setSelectedSubject}
                    setSelectedGrade={setSelectedGrade}
                    handleSaveAndStart={handleSaveAndStart}
                    userName={userName}
                    toggleFocusMode={toggleFocusMode}
                    focusMode={focusMode}
                    handleResetGrade={handleResetGrade}
                    closeMenu={() => setShowMoreMenu(false)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function getMobileUserName(name) {
  const compact = String(name || '').trim().replace(/\s+/g, ' ');
  if (compact.length <= 14) return compact;

  const firstToken = compact.split(' ')[0];
  const emailLocalPart = compact.includes('@') ? compact.split('@')[0] : '';
  const base = emailLocalPart || firstToken || compact;
  if (base.length <= 14) return base;

  return `${base.slice(0, 12)}...`;
}

function AccountMenu({ handleManageAccount, canShareParentFeedback, closeMenu, handleSignOut, navigate }) {
  return (
    <div className="site-nav__menu site-nav__menu--account">
      <button type="button" onClick={handleManageAccount}>Manage Account</button>
      {canShareParentFeedback && (
        <button type="button" onClick={() => { closeMenu(); navigate('/parent/feedback'); }}>
          Share Feedback
        </button>
      )}
      <button
        className="site-nav__signout"
        type="button"
        onClick={async () => {
          closeMenu();
          await handleSignOut();
          navigate('/');
        }}
      >
        Sign Out
      </button>
    </div>
  );
}

function MoreMenu({
  showSelect,
  selectedSubject,
  selectedGrade,
  setSelectedSubject,
  setSelectedGrade,
  handleSaveAndStart,
  userName,
  toggleFocusMode,
  focusMode,
  handleResetGrade,
  closeMenu,
}) {
  return (
    <div className="site-nav__menu site-nav__menu--more">
      <Link className="site-nav__mobileMenuLink" to="/about" onClick={closeMenu}>About</Link>
      <Link className="site-nav__mobileMenuLink" to="/subject" onClick={closeMenu}>Subjects</Link>
      <a className="site-nav__mobileMenuLink" href="mailto:support@tutororca.com" onClick={closeMenu}>Contact</a>
      {!userName && <Link className="site-nav__mobileMenuLink" to="/login" onClick={closeMenu}>Student Login</Link>}

      {showSelect && (
        <div className="site-nav__selector" aria-label="Set quiz subject and grade">
          <strong>Set Your Subject and Grade</strong>
          <label>
            Subject
            <select value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
              <option value="">Select Subject</option>
              <option value="Math">Math</option>
              <option value="Science">Science</option>
            </select>
          </label>
          <label>
            Grade
            <select value={selectedGrade} onChange={e => setSelectedGrade(e.target.value)}>
              <option value="">Select Grade</option>
              {['KG', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map(gr => (
                <option key={gr} value={gr}>
                  {gr === 'KG' ? 'KG' : `${gr} Grade`}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={handleSaveAndStart}>Save and Start Quiz</button>
        </div>
      )}

      {userName && (
        <>
          <button type="button" onClick={toggleFocusMode}>Focus Mode: {focusMode ? 'Enabled' : 'Disabled'}</button>
          <button type="button" onClick={handleResetGrade}>Reset Grade</button>
        </>
      )}
      {!userName && <Link to="/tutor-apply" onClick={closeMenu}>Join as a Tutor</Link>}
      <Link to="/privacy-policy" onClick={closeMenu}>Privacy Policy</Link>
      <Link to="/terms-of-use" onClick={closeMenu}>Terms of Use</Link>
      <Link to="/cookie-policy" onClick={closeMenu}>Cookie Policy</Link>
      <a href="mailto:support@tutororca.com" onClick={closeMenu}>Help</a>
    </div>
  );
}
